import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pill, Plus, X, FileText, Printer } from "lucide-react";

export default function DoctorPrescriptions() {
  const [params] = useSearchParams();
  const initialPatient = params.get("patient_id") || "";
  const [rxs, setRxs] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [vidalQ, setVidalQ] = useState("");
  const [vidalResults, setVidalResults] = useState([]);
  const [form, setForm] = useState({
    patient_id: initialPatient,
    notes: "",
    medications: [],
  });

  const load = async () => {
    try {
      const [{ data: rxData }, { data: patData }] = await Promise.all([
        api.get("/prescriptions"),
        api.get("/patients"),
      ]);
      setRxs(rxData);
      setPatients(patData);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (initialPatient) {
      setForm((f) => ({ ...f, patient_id: initialPatient }));
      setOpen(true);
    }
  }, [initialPatient]);

  const searchVidal = async (q) => {
    setVidalQ(q);
    if (!q) return setVidalResults([]);
    try {
      const { data } = await api.get("/vidal/search", { params: { q } });
      setVidalResults(data);
    } catch (e) {
      // ignore
    }
  };

  const addMed = (m) => {
    setForm((f) => ({
      ...f,
      medications: [...f.medications, { name: m.name, posology: m.common_posology, duration: "7 jours", quantity: 1 }],
    }));
    setVidalResults([]);
    setVidalQ("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.medications.length === 0) return toast.error("Ajoutez au moins un médicament");
    try {
      await api.post("/prescriptions", form);
      toast.success("Ordonnance signée et archivée");
      setOpen(false);
      setForm({ patient_id: "", notes: "", medications: [] });
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const updateMed = (i, k, v) => {
    const meds = [...form.medications];
    meds[i] = { ...meds[i], [k]: v };
    setForm({ ...form, medications: meds });
  };

  const removeMed = (i) => {
    const meds = form.medications.filter((_, idx) => idx !== i);
    setForm({ ...form, medications: meds });
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-2">Vidal · Ordonnances</div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Mes ordonnances</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="btn-primary" data-testid="new-rx-btn"><Plus className="h-4 w-4 mr-2" />Nouvelle ordonnance</Button>
      </div>

      {rxs.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-300 rounded-xl text-slate-500">
          <Pill className="mx-auto h-10 w-10 text-slate-300 mb-3" />
          Aucune ordonnance pour le moment.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {rxs.map((r) => (
            <div key={r.prescription_id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`rx-${r.prescription_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-semibold">{r.patient?.name}</div>
                  <div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString("fr-FR", { dateStyle: "long" })}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => setPreview(r)} data-testid={`preview-rx-${r.prescription_id}`}><FileText className="h-3.5 w-3.5 mr-1" />Voir</Button>
              </div>
              <ul className="mt-3 space-y-1 text-sm">
                {r.medications.slice(0, 3).map((m, i) => (
                  <li key={i} className="text-slate-700">• {m.name} — <span className="text-slate-500">{m.posology}</span></li>
                ))}
                {r.medications.length > 3 && <li className="text-xs text-slate-400">+{r.medications.length - 3} autres…</li>}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-display">Nouvelle ordonnance</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-5 mt-2">
            <div>
              <Label>Patient</Label>
              <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="rx-patient-select"><SelectValue placeholder="Sélectionner un patient" /></SelectTrigger>
                <SelectContent>
                  {patients.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name} · {p.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Rechercher un médicament (base Vidal)</Label>
              <Input value={vidalQ} onChange={(e) => searchVidal(e.target.value)} placeholder="Doliprane, amoxicilline…" className="mt-1.5" data-testid="vidal-search-input" />
              {vidalResults.length > 0 && (
                <div className="mt-2 border border-slate-200 rounded-md max-h-48 overflow-auto">
                  {vidalResults.map((m) => (
                    <button type="button" key={m.code} onClick={() => addMed(m)} data-testid={`vidal-add-${m.code}`} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 text-sm">
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-slate-500">{m.form} · {m.common_posology}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Prescription</Label>
              {form.medications.length === 0 && <p className="text-sm text-slate-400">Aucun médicament. Recherchez ci-dessus.</p>}
              {form.medications.map((m, i) => (
                <div key={i} className="border border-slate-200 rounded-md p-3 space-y-2">
                  <div className="flex items-start justify-between">
                    <Input value={m.name} onChange={(e) => updateMed(i, "name", e.target.value)} className="font-medium flex-1" />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeMed(i)}><X className="h-4 w-4" /></Button>
                  </div>
                  <Input value={m.posology} onChange={(e) => updateMed(i, "posology", e.target.value)} placeholder="Posologie" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={m.duration} onChange={(e) => updateMed(i, "duration", e.target.value)} placeholder="Durée" />
                    <Input type="number" value={m.quantity} onChange={(e) => updateMed(i, "quantity", parseInt(e.target.value) || 1)} placeholder="Quantité" />
                  </div>
                </div>
              ))}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5" />
            </div>

            <Button type="submit" className="w-full btn-primary" data-testid="rx-submit-btn">Signer et archiver</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview dialog (printable) */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Printer className="h-5 w-5" />Ordonnance</DialogTitle></DialogHeader>
          {preview && (
            <div className="border border-slate-300 p-8 bg-white text-slate-900 font-mono text-sm">
              <div className="border-b border-slate-300 pb-4 mb-4">
                <div className="font-display font-bold text-lg">Dr {preview.doctor?.name?.replace(/^Dr\s+/, "")}</div>
                <div className="text-xs">{preview.doctor?.specialty}</div>
                <div className="text-xs">{preview.doctor?.address}</div>
                <div className="text-xs mt-1">{preview.doctor?.phone}</div>
              </div>
              <div className="flex justify-between text-xs mb-6">
                <div>
                  <div className="font-bold">Patient :</div>
                  <div>{preview.patient?.name}</div>
                </div>
                <div className="text-right">
                  <div>{new Date(preview.created_at).toLocaleDateString("fr-FR", { dateStyle: "long" })}</div>
                </div>
              </div>
              <div className="font-display font-bold text-xl mb-4">℞</div>
              <ol className="list-decimal list-inside space-y-3">
                {preview.medications.map((m, i) => (
                  <li key={i}>
                    <span className="font-semibold">{m.name}</span><br />
                    <span className="text-xs ml-4">Posologie : {m.posology}</span><br />
                    <span className="text-xs ml-4">Durée : {m.duration} · Qté : {m.quantity}</span>
                  </li>
                ))}
              </ol>
              {preview.notes && <div className="mt-6 text-xs"><div className="font-bold">Notes :</div>{preview.notes}</div>}
              <div className="mt-10 text-right text-xs">
                <div>Signature et tampon</div>
                <div className="font-display font-bold mt-2">Dr {preview.doctor?.name?.replace(/^Dr\s+/, "")}</div>
              </div>
            </div>
          )}
          <Button onClick={() => window.print()} variant="outline" data-testid="rx-print-btn"><Printer className="h-4 w-4 mr-2" />Imprimer</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
